import React, { useState } from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import type { ImageSourceEncoding } from '../../../hooks/useImageSource';
import { useImageSource } from '../../../hooks/useImageSource';

/* Never reached: the hook runs a loader only for a key, and there is no key without one. */
const NO_LOAD = () => new Blob();

export type ImageProps = {
  alt?: string;
  className?: string;
  /** @see useImageSource - only a picture bound for a PDF needs to say anything. */
  encode?: ImageSourceEncoding;
  /**
   * Drawn instead of the picture - when there is none to draw, and when the one there was refused
   * to decode. A node rather than a url, because what stands in for a missing picture is never the
   * same twice: initials, a badge, another picture, or nothing at all.
   */
  fallback?: React.ReactNode;
  /**
   * **Together with `load`, for bytes that have to be fetched** - an authenticated endpoint, or a
   * file the reader has just picked. Left off, `src` is handed to the browser, which is what an
   * ordinary url wants: cached, shared between pages, and needing no permission of its own.
   */
  imageKey?: string | null;
  load?: () => Blob | Promise<Blob>;
  name?: string;
  /**
   * The failure is already handled - `fallback` is drawn either way. This is for a caller that has
   * to *do* something about it as well.
   */
  onError?: () => void;
  src?: string | null;
  title?: string;
  width?: string;
};

/**
 * A picture, and what to draw when there is not one. **The failure only an `<img>` can see** - bytes
 * that arrived and would not decode - is caught here rather than by every caller in turn.
 *
 * It holds no shape of its own: cropping, circles and sizes belong to whatever is using it.
 */
export const Image = ({
  alt = '',
  className,
  encode,
  fallback = null,
  imageKey,
  load,
  name,
  onError,
  src,
  title,
  width,
}: ImageProps) => {
  /*
    Which source failed, rather than a flag: a new one is a fresh chance and has to clear the last
    one's failure without an effect to reset it.
  */
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  // Inert without a loader - a null key never loads, so an ordinary url does no fetching at all.
  const { source: fetched } = useImageSource({
    encode,
    imageKey: load ? (imageKey ?? null) : null,
    load: load ?? NO_LOAD,
  });

  const source = load ? fetched : (src ?? null);

  // Not JSX: a fragment around one child is a lint error, and `fallback` is already a node.
  if (!source || failedSrc === source) return fallback;

  return (
    <img
      alt={alt}
      className={AttrsHelper.formatClassname(className)}
      data-test={name}
      onError={() => {
        setFailedSrc(source);
        onError?.();
      }}
      src={source}
      style={width ? { width } : undefined}
      title={title}
    />
  );
};
